import styles from './CTATextContent.module.scss';
import { Colors } from '../../../theme/Colors';
import { Heading, HeadingLevel } from '../../display/Heading';
import { LegacyButton } from '../../navigation/LegacyButton/Button';
import { DeprecatedBgColor, BgColor, CTABtnColor } from './types';
import { TextColorMap } from './constants';
import { getCtaBtnColor } from './utils';
import { AnchorTarget } from '../../../ts-types';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

type Alignment = 'left' | 'center' | 'right';

export type Props = {
  heading: React.ReactNode;
  subHeading?: React.ReactNode;
  description?: React.ReactNode;
  ctaText?: React.ReactNode;
  ctaHref?: string;
  ctaBtnColor?: CTABtnColor;
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  bgColor: DeprecatedBgColor | BgColor;
  alignment?: Alignment;
  ctaTarget?: AnchorTarget;
};

const getTextColor = (bgColor: BgColor | DeprecatedBgColor) => {
  const mappedColor = TextColorMap[bgColor];
  return Colors[LegacyColorMap[mappedColor]];
};

const getButton = (props: Props) => {
  const btnProps = {
    children: props.ctaText,
    href: props.ctaHref,
    onClick: props.onCtaClick,
    onKeyDown: props.onCtaKeyDown,
    target: props.ctaTarget,
    'data-testid': 'cta-button',
  };
  const btnColor = getCtaBtnColor(props.bgColor, props.ctaBtnColor);
  return (
    <LegacyButton
      {...btnProps}
      // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
      color={btnColor === 'primary-alt' ? 'primary' : btnColor}
      size="large"
    />
  );
};

/**
 * Internal component for text content in `CTAStandard` and `CTAFilled` that
 * shares the same structure and styling.
 */
export const CTATextContent = (props: Props) => {
  const textColor = getTextColor(props.bgColor);

  return (
    <div
      className={styles.root}
      data-align={props.alignment || 'left'}
      style={{
        '--_zds-cta-text-content-text-color': textColor,
      }}
      data-zds
    >
      <HeadingLevel>
        <Heading className={styles.heading}>{props.heading}</Heading>
        {props.subHeading && (
          <HeadingLevel>
            <Heading className={styles.subHeading}>{props.subHeading}</Heading>
          </HeadingLevel>
        )}
      </HeadingLevel>
      {props.description && (
        <div
          className={styles.description}
          data-align={props.alignment}
          data-zds
        >
          {props.description}
        </div>
      )}
      {props.ctaText && (props.ctaHref || props.onCtaClick) && (
        <div className={styles.ctaContainer} data-zds>
          {getButton(props)}
        </div>
      )}
    </div>
  );
};
